import { useEffect, useState } from 'react';
import { api } from '../services/apiClient';
import './GoldTrendRadarPage.css';

const REFRESH_INTERVAL = 300_000; // 5 分钟（与后端缓存同步）

// 趋势状态 → 颜色映射
const STATUS_COLORS = {
  强多: { color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
  偏多: { color: '#86efac', bg: 'rgba(134,239,172,0.10)' },
  震荡: { color: '#fbbf24', bg: 'rgba(251,191,36,0.10)' },
  偏空: { color: '#fca5a5', bg: 'rgba(252,165,165,0.10)' },
  强空: { color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
};

function fmtPrice(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// 进度条上的刻度标签
const SCORE_LABELS = [
  { label: '强空', score: 10 },
  { label: '偏空', score: 30 },
  { label: '震荡', score: 50 },
  { label: '偏多', score: 70 },
  { label: '强多', score: 90 },
];

export default function GoldTrendRadarPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const result = await api.getGoldVegasTrend();
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || '黄金趋势数据暂时不可用');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    const timer = setInterval(load, REFRESH_INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  if (error && !data) {
    return (
      <main className="pro-page gtr-page">
        <div className="pro-page-inner">
          <header className="pro-hero">
            <p className="pro-hero-label">Gold Trend Radar</p>
            <h1>黄金4小时趋势雷达</h1>
            <span className="pro-hero-desc">
              基于维加斯通道 EMA144 / 169 / 576 / 676 判断黄金中期趋势
            </span>
          </header>
          <div className="gtr-error">{error}</div>
        </div>
      </main>
    );
  }

  const statusStyle = data ? STATUS_COLORS[data.status] : null;

  return (
    <main className="pro-page gtr-page">
      <div className="pro-page-inner">
        <header className="pro-hero">
          <p className="pro-hero-label">Gold Trend Radar</p>
          <h1>黄金4小时趋势雷达</h1>
          <span className="pro-hero-desc">
            基于维加斯通道 EMA144 / 169 / 576 / 676 判断黄金中期趋势
          </span>
        </header>

        {loading && !data && <div className="gtr-loading">正在加载趋势数据…</div>}

        {data && (
          <>
            {/* 趋势状态大卡 */}
            <section
              className="gtr-status-card"
              style={{
                borderColor: statusStyle?.color || 'var(--border-default)',
                background: statusStyle?.bg || 'var(--bg-card)',
              }}
            >
              <div className="gtr-status-top">
                <span className="gtr-status-label">当前趋势</span>
                <span className="gtr-status-price">${fmtPrice(data.price)}</span>
              </div>
              <div
                className="gtr-status-badge"
                style={{ color: statusStyle?.color || 'var(--text-primary)' }}
              >
                {data.status}
              </div>
              <div className="gtr-status-scorebar">
                {SCORE_LABELS.map((s) => (
                  <span
                    key={s.label}
                    className={`gtr-score-tick${data.score >= s.score ? ' active' : ''}`}
                  >
                    {s.label}
                  </span>
                ))}
                <div className="gtr-score-track">
                  <div
                    className="gtr-score-fill"
                    style={{
                      width: `${Math.max(5, data.score)}%`,
                      background: statusStyle?.color || '#fbbf24',
                    }}
                  />
                  <div
                    className="gtr-score-pointer"
                    style={{
                      left: `${Math.max(3, data.score)}%`,
                      borderColor: statusStyle?.color || '#fbbf24',
                    }}
                  />
                </div>
              </div>
            </section>

            {/* 通道数据 */}
            <section className="gtr-channels">
              <article className="gtr-channel-card">
                <h2>短周期通道</h2>
                <p className="gtr-channel-desc">EMA 144 / 169</p>
                <div className="gtr-channel-grid">
                  <div className="gtr-channel-cell">
                    <span>上轨</span>
                    <strong>{fmtPrice(data.channels.fast.upper)}</strong>
                  </div>
                  <div className="gtr-channel-cell">
                    <span>下轨</span>
                    <strong>{fmtPrice(data.channels.fast.lower)}</strong>
                  </div>
                  <div className="gtr-channel-cell">
                    <span>EMA144</span>
                    <strong>{fmtPrice(data.channels.fast.ema144)}</strong>
                  </div>
                  <div className="gtr-channel-cell">
                    <span>EMA169</span>
                    <strong>{fmtPrice(data.channels.fast.ema169)}</strong>
                  </div>
                </div>
                <div className="gtr-channel-slope">
                  方向：<strong>{data.slopes.fast}</strong>
                </div>
              </article>
              <article className="gtr-channel-card">
                <h2>长周期通道</h2>
                <p className="gtr-channel-desc">EMA 576 / 676</p>
                <div className="gtr-channel-grid">
                  <div className="gtr-channel-cell">
                    <span>上轨</span>
                    <strong>{fmtPrice(data.channels.slow.upper)}</strong>
                  </div>
                  <div className="gtr-channel-cell">
                    <span>下轨</span>
                    <strong>{fmtPrice(data.channels.slow.lower)}</strong>
                  </div>
                  <div className="gtr-channel-cell">
                    <span>EMA576</span>
                    <strong>{fmtPrice(data.channels.slow.ema576)}</strong>
                  </div>
                  <div className="gtr-channel-cell">
                    <span>EMA676</span>
                    <strong>{fmtPrice(data.channels.slow.ema676)}</strong>
                  </div>
                </div>
                <div className="gtr-channel-slope">
                  方向：<strong>{data.slopes.slow}</strong>
                </div>
              </article>
            </section>

            {/* 解读 */}
            <section className="gtr-summary-card">
              <h2>📈 趋势解读</h2>
              <p>{data.summary}</p>
            </section>

            {/* 更新时间 */}
            {data.updatedAt && (
              <p className="gtr-updated">
                数据更新于 {new Date(data.updatedAt).toLocaleString('zh-CN')} · 每 5 分钟自动刷新
              </p>
            )}
          </>
        )}
      </div>
    </main>
  );
}
